/**
 * Survey service for Hugo_Surveys Firestore collection.
 *
 * Document structure:
 * - organizationId: string
 * - title: string
 * - description: string
 * - status: 'draft' | 'active' | 'closed'
 * - targetScope: map { type: 'organization' | 'site' | 'department' | 'team', id: string }
 * - createdAt: Timestamp
 * - createdBy: string (uid)
 * - dueDate: string (optional, ISO date string)
 * - questionSet: 'standard' | 'custom'
 * - anonymous: boolean
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { logActivity } from '../utils/firestore';

const COLLECTION_NAME = 'Hugo_Surveys';

/** Valid status values */
export const SURVEY_STATUSES = ['draft', 'active', 'closed'];

/** Valid target scope types */
export const TARGET_SCOPE_TYPES = ['organization', 'site', 'department', 'team'];

/** Valid question set types */
export const QUESTION_SET_TYPES = ['standard', 'custom'];

/**
 * Create a new survey
 */
export async function createSurvey({
  organizationId,
  title,
  description,
  status = 'draft',
  targetScope,
  createdBy,
  dueDate = null,
  questionSet = 'standard',
  anonymous = false,
}) {
  if (!organizationId) throw new Error('organizationId is required');
  if (!title) throw new Error('title is required');
  if (!createdBy) throw new Error('createdBy is required');
  if (!SURVEY_STATUSES.includes(status)) {
    throw new Error(`Invalid status: ${status}. Must be one of: ${SURVEY_STATUSES.join(', ')}`);
  }
  if (questionSet && !QUESTION_SET_TYPES.includes(questionSet)) {
    throw new Error(`Invalid questionSet: ${questionSet}. Must be one of: ${QUESTION_SET_TYPES.join(', ')}`);
  }

  const surveyData = {
    organizationId,
    title,
    description: description || '',
    status,
    targetScope: targetScope || { type: 'organization', id: organizationId },
    createdAt: Timestamp.now(),
    createdBy,
    dueDate: dueDate || null,
    questionSet: questionSet || 'standard',
    anonymous: anonymous || false,
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), surveyData);

  // Log survey creation activity
  logActivity(createdBy, 'survey_created', 'drift-accumulation', {
    surveyId: docRef.id,
    title,
    status,
    organizationId,
  });

  return { id: docRef.id, ...surveyData };
}

/**
 * Get a survey by ID
 */
export async function getSurvey(surveyId) {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    return null;
  }
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Get all surveys for an organization
 */
export async function getSurveysForOrganization(organizationId) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get surveys by status for an organization
 */
export async function getSurveysByStatus(organizationId, status) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    where('status', '==', status),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to real-time updates for surveys in an organization
 */
export function subscribeToSurveys(organizationId, callback) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const surveys = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(surveys);
  });
}

/**
 * Subscribe to a single survey's real-time updates
 */
export function subscribeToSurvey(surveyId, callback) {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    } else {
      callback(null);
    }
  });
}

/**
 * Update survey fields
 */
export async function updateSurvey(surveyId, updates) {
  // Validate status if being updated
  if (updates.status && !SURVEY_STATUSES.includes(updates.status)) {
    throw new Error(`Invalid status: ${updates.status}. Must be one of: ${SURVEY_STATUSES.join(', ')}`);
  }
  // Validate questionSet if being updated
  if (updates.questionSet && !QUESTION_SET_TYPES.includes(updates.questionSet)) {
    throw new Error(`Invalid questionSet: ${updates.questionSet}. Must be one of: ${QUESTION_SET_TYPES.join(', ')}`);
  }

  const docRef = doc(db, COLLECTION_NAME, surveyId);
  await updateDoc(docRef, updates);
}

/**
 * Transition survey status: draft -> active -> closed
 * Validates that the transition is allowed.
 */
export async function transitionSurveyStatus(surveyId, newStatus) {
  const survey = await getSurvey(surveyId);
  if (!survey) throw new Error('Survey not found');

  const validTransitions = {
    draft: ['active'],
    active: ['closed'],
    closed: ['active'], // Allow reopening a closed survey
  };

  const allowed = validTransitions[survey.status] || [];
  if (!allowed.includes(newStatus)) {
    throw new Error(
      `Cannot transition from '${survey.status}' to '${newStatus}'. Allowed transitions: ${allowed.join(', ') || 'none'}`
    );
  }

  await updateSurvey(surveyId, { status: newStatus });

  // Log status transition activity
  logActivity(survey.createdBy || 'system', 'survey_status_changed', 'drift-accumulation', {
    surveyId,
    title: survey.title,
    fromStatus: survey.status,
    toStatus: newStatus,
  });

  return { ...survey, status: newStatus };
}

/**
 * Delete a survey
 */
export async function deleteSurvey(surveyId) {
  const docRef = doc(db, COLLECTION_NAME, surveyId);
  await deleteDoc(docRef);
}

/**
 * Get all surveys (superadmin view)
 */
export async function getAllSurveys() {
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export { COLLECTION_NAME };
